import React, { useState, useMemo, useEffect } from 'react';
import { formatExeclNormalPercent, formatPercentNum } from '@/pages/dashboard/utils/utils';
import { connect, getLocale, useIntl } from 'umi';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import { addChartLabel } from '@/pages/dashboard/components/Chart/_config/plug';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import ChurnChartCo from '../Nightingale/component/ChurnChartCo';
import UserFilter from './UserFilter';

const TrackingDayCo = (props: any) => {
  const locale = getLocale();
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEN = locale === 'en-US';
  const { filterVal, config: propsConfig, data: apiData, explainInfo, apiDataKey, changeData, dataKey } = props;

  const { timeType, rangeTime } = filterVal;
  const { metricsKey, title: titleEn, titleCn, tab, titleDes: titleDesConfig, defaultActive = 2, hideConfig, height } = propsConfig || {};
  const [tabName, setTabName] = useState('');
  const [isEye, setIsEye] = useState(true);
  const [userParams, setUserParams] = useState({});

  useEffect(()=> {
    if(rangeTime && Object.keys(userParams).length === 0){
      setUserParams({
        dayOne: rangeTime?.[0],
        dayTwo: rangeTime?.[1],
        trackingDay: ['2'],
      });
    }
  },[rangeTime, userParams]);

  const title = isEN ? titleEn : titleCn;


  const data = useMemo(()=> {
    const metricsData = apiData?.[apiDataKey] || {};
    let isLoading = true;
    let isError = false;
    let isNoData = false;
    let componentData : any = [];
    if(metricsData){
      componentData = metricsKey ? metricsData?.data?.[metricsKey] : metricsData?.data || [];
      isLoading = metricsData?.loading;
      isError = metricsData?.errorMsg;
      isNoData = componentData?.length <= 0;
    }
    return {
      componentData,
      isLoading,
      isError,
      isNoData
    };
  },[apiData, apiDataKey, metricsKey]);

  const tabList = tab?.list?.map((v: { text: string; textCn: string; value: string | number; }) => ({
    text: isEN ? v.text : v.textCn,
    value: v.value
  }));
  
  const titleDes = titleDesConfig?.map((val)=> {
    const explain = getExplainVal(explainInfo, val?.rainbowKey, getLocale(), val?.explainEn, val?.explainCn);
    const content = isEN ? val.contentEn : val.contentCn;

    if(!explain && !content){
      return null;
    }

    return {
      title: isEN ? val.title : val.titleCn,
      content: val.isRainbow ? explain : content
    };
  }).filter((val : { title: string; content: string; } | null) => val !== null);


  // 折线图 && 表格
  const options = useMemo(() => {
    const options: Option = {};
    const optionData = data?.componentData || [];

    options.chart = optionData?.[0]?.['kv_list']?.map?.((item, index) => {
      const data = optionData?.map?.(dataItem => ({
        x: dataItem?.['level'],
        y: dataItem?.['kv_list']?.[index]?.value
      }));
      const option = {
        name: item.key,
        data
      };
      return option;
    }) || [];
    options.table = {
      option: [
        {
          key: 'level',
          align: 'right',
          fixed: 'left',
          width: 100,
          header: formatMessage({ id: 'common_level' }),
        },
        {
          key: 'dayOne',
          align: 'right',
          fixed: 'left',
          width: 100,
          header: `${optionData?.[0]?.['kv_list']?.[0]?.key} ${  formatMessage({ id: 'engagement_churn_Churn%' })}`,
          render: (record) => formatPercentNum(record?.['dayOne'])
        },
        {
          key: 'dayTwo',
          align: 'right',
          fixed: 'left',
          width: 100,
          header: `${optionData?.[0]?.['kv_list']?.[1]?.key} ${  formatMessage({ id: 'engagement_churn_Churn%' })}`,
          render: (record) => formatPercentNum(record?.['dayTwo'])
        },
      ],
      data: optionData?.map?.(item => ({
        level: item?.['level'],
        dayOne: item?.['kv_list']?.[0]?.value,
        dayTwo: item?.['kv_list']?.[1]?.value,
      })),
      downloadData: optionData?.map?.(item => ({
        level: item?.['level'],
        dayOne: formatExeclNormalPercent(item?.['kv_list']?.[0]?.value),
        dayTwo: formatExeclNormalPercent(item?.['kv_list']?.[1]?.value),
      }))
    };
    return options;
  }, [data?.componentData, isEN]);

  const axisLabelFormat = (opt: any) => opt.xAxis?.map((item: any) => ({
    ...item,
    axisLabel: {
      ...item.axisLabel,
      formatter: (value: any) => `L${value}`,
    },
  }));

  const handleAxisFormat = (value: any) => `L${value}`;

  const setOtherConfigFunc = (opt, timeType) => {
    opt.yAxis.axisLabel.formatter = (value) => formatPercentNum(value, 2);
    // 格式化横坐标 label
    opt.xAxis = axisLabelFormat(opt, timeType); 

    opt = addChartLabel(opt, {
      formatter: (params) => {
        const result = formatPercentNum(params?.value, false);
        return result;
      },
      show: isEye,
      formatNum: (value) => formatPercentNum(value)
    });
    return opt;
  };

  const formatterFunc = (params) => {
    const title = formatMessage({ id: 'engagement_churn_Tracking_Day' }) + userParams?.trackingDay?.[0];
    return getEchartTooltipText({
      title,
      data: params?.map(item => ({
        name: item.seriesName,
        metricsNum: formatPercentNum(item?.data?.value),
        color: item.color
      }))
    });
  };

  const onFilterChange = (val: any) => {
    setUserParams(val);
    changeData(dataKey, {
      first_node: val?.dayOne || userParams?.dayOne,
      second_node: val?.dayTwo || userParams?.dayTwo,
      track_days: val?.trackingDay || userParams?.trackingDay,
    });
  };

  return (
    <>
      <ChurnChartCo
        options={options}
        tabName={tabName}
        setTabName={setTabName}
        timeType={timeType}
        tabList={tabList}
        title={title}
        titleDes={titleDes}
        isLoading={data.isLoading}
        isError={data.isError}
        isNoData={data.isNoData}
        defaultActive={defaultActive}
        formatterFunc={formatterFunc}
        setOtherConfigFunc={setOtherConfigFunc}
        isEye={isEye}
        setIsEye={setIsEye}
        hideConfig={hideConfig}
        filterSlot={<UserFilter formValue={userParams} onChange={onFilterChange} />}
        handleAxisFormat={handleAxisFormat}
        height={height}
        defa
      />
    </>
  );
};


export default connect((state: any) => ({
  explainInfo: state.dashboard?.explainInfo,
}))(TrackingDayCo);
